import { RefObject, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  HelperText,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextInput,
} from '@c2fo/liquidity';
import { US_STATES, UsStateCode } from '@/enterprise/components/InstantPay/usStates';
import useUserDetails from '@/shared/data/useUserDetails';
import { ANNUAL_REVENUE_BANDS, BUSINESS_DETAILS_CONTENT } from './content';
import { AnnualRevenueRangeId, BusinessDetailsDraft } from './quoteWizardState';

interface BusinessDetailsFormValues {
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  companyName: string;
  addressLine1: string;
  city: string;
  // Empty string is the "nothing picked yet" state; `required` below rejects it.
  state: UsStateCode | '';
  zipCode: string;
  revenueRange: AnnualRevenueRangeId | '';
}

interface BusinessDetailsStepProps {
  /** Previously submitted values, so returning from step 2 does not clear the form. */
  draft: BusinessDetailsDraft | null;
  onSubmit: (draft: BusinessDetailsDraft) => void;
  onValidityChange: (isValid: boolean) => void;
  /**
   * Filled with this form's submit handler. The wizard's Next button is portalled to the page
   * bottom, outside this form, and a `<button form="...">` association does not submit under jsdom
   * — so the footer calls the handler directly instead.
   */
  submitRef: RefObject<(() => void) | null>;
}

const emptyValues: BusinessDetailsFormValues = {
  firstName: '',
  lastName: '',
  emailAddress: '',
  phoneNumber: '',
  companyName: '',
  addressLine1: '',
  city: '',
  state: '',
  zipCode: '',
  revenueRange: '',
};

// Deliberately permissive — one @, a dot in the domain, no spaces. Anything stricter rejects
// addresses that are actually deliverable, and SEA is the real authority on this.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Digit count only, so a prefilled value keeps whatever separators and country code the API returned
// instead of failing validation on a field the supplier never touched.
const validatePhoneNumber = (value: string): string | true =>
  value.replace(/\D/g, '').length >= 10 ? true : BUSINESS_DETAILS_CONTENT.errors.phoneNumber;

export const BusinessDetailsStep = ({ draft, onSubmit, onValidityChange, submitRef }: BusinessDetailsStepProps) => {
  // The logged-in user is the contact by default. Unlike the business fields, this is the user's own
  // information rather than a guess at a legal entity, so prefilling it cannot be quietly wrong.
  const { data: contact } = useUserDetails((data) => ({
    firstName: data.me?.personalInfo?.firstName ?? '',
    lastName: data.me?.personalInfo?.lastName ?? '',
    emailAddress: data.me?.emailAddress ?? '',
    phoneNumber: data.me?.personalInfo?.primaryPhoneNumber ?? '',
  }));

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<BusinessDetailsFormValues>({
    // The footer Next button is disabled until the form is valid, so validity has to track typing.
    mode: 'onChange',
    // A draft wins outright — it already holds whatever the supplier edited the contact fields to.
    // The business fields stay empty: the source for a legal entity name and a trustworthy
    // registered address is still open.
    defaultValues: draft ?? { ...emptyValues, ...contact },
  });

  useEffect(() => {
    onValidityChange(isValid);
  }, [isValid, onValidityChange]);

  // RHF registers in call order and focuses the first invalid field on submit, so these follow the
  // on-screen order of the text fields. Annual revenue (6th) and state (9th) use Controller and
  // cannot take that focus.
  const firstNameField = register('firstName', {
    required: BUSINESS_DETAILS_CONTENT.errors.firstName,
    setValueAs: (value: string) => value.trim(),
  });
  const lastNameField = register('lastName', {
    required: BUSINESS_DETAILS_CONTENT.errors.lastName,
    setValueAs: (value: string) => value.trim(),
  });
  const emailAddressField = register('emailAddress', {
    required: BUSINESS_DETAILS_CONTENT.errors.emailAddress,
    pattern: { value: EMAIL_PATTERN, message: BUSINESS_DETAILS_CONTENT.errors.emailAddress },
    setValueAs: (value: string) => value.trim(),
  });
  const phoneNumberField = register('phoneNumber', {
    required: BUSINESS_DETAILS_CONTENT.errors.phoneNumber,
    validate: validatePhoneNumber,
    setValueAs: (value: string) => value.trim(),
  });
  const companyNameField = register('companyName', {
    required: BUSINESS_DETAILS_CONTENT.errors.companyName,
    setValueAs: (value: string) => value.trim(),
  });
  const addressLine1Field = register('addressLine1', {
    required: BUSINESS_DETAILS_CONTENT.errors.addressLine1,
    setValueAs: (value: string) => value.trim(),
  });
  const cityField = register('city', {
    required: BUSINESS_DETAILS_CONTENT.errors.city,
    setValueAs: (value: string) => value.trim(),
  });
  const zipCodeField = register('zipCode', {
    required: BUSINESS_DETAILS_CONTENT.errors.zipCode,
    pattern: { value: /^\d{5}(-\d{4})?$/, message: BUSINESS_DETAILS_CONTENT.errors.zipCode },
    setValueAs: (value: string) => value.trim(),
  });

  const submit = handleSubmit((values) => {
    // Both are validated as required, so neither can be the empty placeholder here.
    if (values.state === '' || values.revenueRange === '') return;

    onSubmit({
      firstName: values.firstName,
      lastName: values.lastName,
      emailAddress: values.emailAddress,
      phoneNumber: values.phoneNumber,
      companyName: values.companyName,
      addressLine1: values.addressLine1,
      city: values.city,
      state: values.state,
      zipCode: values.zipCode,
      revenueRange: values.revenueRange,
    });
  });

  // No dependency list: `submit` is a new closure each render and the footer must always call the
  // current one.
  useEffect(() => {
    submitRef.current = submit;
    return () => {
      submitRef.current = null;
    };
  });

  return (
    <form onSubmit={submit} noValidate className="mx-auto max-w-5xl space-y-6 rounded-xl bg-white p-6 shadow-sm">
      <div className="space-y-1">
        <h2 className="m-0 p-0 text-xl font-bold text-text-primary">{BUSINESS_DETAILS_CONTENT.heading}</h2>
        <p className="m-0 p-0 text-sm text-text-secondary">{BUSINESS_DETAILS_CONTENT.description}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor="firstName" required>
            {BUSINESS_DETAILS_CONTENT.fields.firstName.label}
          </Label>
          <TextInput id="firstName" className="w-full" hasError={!!errors.firstName} {...firstNameField} />
          {errors.firstName?.message && <HelperText variant="error">{errors.firstName.message}</HelperText>}
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="lastName" required>
            {BUSINESS_DETAILS_CONTENT.fields.lastName.label}
          </Label>
          <TextInput id="lastName" className="w-full" hasError={!!errors.lastName} {...lastNameField} />
          {errors.lastName?.message && <HelperText variant="error">{errors.lastName.message}</HelperText>}
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="emailAddress" required>
            {BUSINESS_DETAILS_CONTENT.fields.emailAddress.label}
          </Label>
          <TextInput
            id="emailAddress"
            type="email"
            className="w-full"
            hasError={!!errors.emailAddress}
            {...emailAddressField}
          />
          {errors.emailAddress?.message && <HelperText variant="error">{errors.emailAddress.message}</HelperText>}
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="phoneNumber" required>
            {BUSINESS_DETAILS_CONTENT.fields.phoneNumber.label}
          </Label>
          <TextInput
            id="phoneNumber"
            type="tel"
            className="w-full"
            hasError={!!errors.phoneNumber}
            {...phoneNumberField}
          />
          {errors.phoneNumber?.message && <HelperText variant="error">{errors.phoneNumber.message}</HelperText>}
        </div>
      </div>

      <div className="border-t border-stroke" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor="companyName" required>
            {BUSINESS_DETAILS_CONTENT.fields.companyName.label}
          </Label>
          <TextInput id="companyName" className="w-full" hasError={!!errors.companyName} {...companyNameField} />
          {errors.companyName?.message && <HelperText variant="error">{errors.companyName.message}</HelperText>}
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="revenueRange" required>
            {BUSINESS_DETAILS_CONTENT.fields.revenueRange.label}
          </Label>
          <Controller
            control={control}
            name="revenueRange"
            rules={{ required: BUSINESS_DETAILS_CONTENT.errors.revenueRange }}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger
                  id="revenueRange"
                  aria-label={BUSINESS_DETAILS_CONTENT.fields.revenueRange.label}
                  hasError={!!errors.revenueRange}
                >
                  <SelectValue placeholder={BUSINESS_DETAILS_CONTENT.fields.revenueRange.placeholder} />
                </SelectTrigger>
                <SelectContent>
                  {ANNUAL_REVENUE_BANDS.map(({ id, label }) => (
                    <SelectItem key={id} value={id}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.revenueRange?.message && <HelperText variant="error">{errors.revenueRange.message}</HelperText>}
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="addressLine1" required>
            {BUSINESS_DETAILS_CONTENT.fields.addressLine1.label}
          </Label>
          <TextInput id="addressLine1" className="w-full" hasError={!!errors.addressLine1} {...addressLine1Field} />
          {errors.addressLine1?.message && <HelperText variant="error">{errors.addressLine1.message}</HelperText>}
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="city" required>
            {BUSINESS_DETAILS_CONTENT.fields.city.label}
          </Label>
          <TextInput id="city" className="w-full" hasError={!!errors.city} {...cityField} />
          {errors.city?.message && <HelperText variant="error">{errors.city.message}</HelperText>}
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="state" required>
            {BUSINESS_DETAILS_CONTENT.fields.state.label}
          </Label>
          <Controller
            control={control}
            name="state"
            rules={{ required: BUSINESS_DETAILS_CONTENT.errors.state }}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger
                  id="state"
                  aria-label={BUSINESS_DETAILS_CONTENT.fields.state.label}
                  hasError={!!errors.state}
                >
                  <SelectValue placeholder={BUSINESS_DETAILS_CONTENT.fields.state.placeholder} />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map(({ code, name }) => (
                    <SelectItem key={code} value={code}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.state?.message && <HelperText variant="error">{errors.state.message}</HelperText>}
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="zipCode" required>
            {BUSINESS_DETAILS_CONTENT.fields.zipCode.label}
          </Label>
          <TextInput id="zipCode" className="w-full" hasError={!!errors.zipCode} {...zipCodeField} />
          {errors.zipCode?.message && <HelperText variant="error">{errors.zipCode.message}</HelperText>}
        </div>
      </div>
    </form>
  );
};
